import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
// Models
import { User, Organization, Pilot, Vehicle, FlightSession, Device, MultipleApiResponse } from '@mocca-technologies/model';

import { VEHICLE, PILOT, GET_ALL_PILOT, GET_ALL_VEHICLE} from '../utils/routing/routes.model';
// Components
import { HTTPResponseModalComponent } from '../components/http-response-modal/http-response-modal.component';

@Injectable({
    providedIn: 'root'
})

export class CoreService {
  private readonly DEBUG = false;

  navigateTo: Subject<string> = new Subject();

  restrictedOrganizations = {
    'Technical University of Catalonia': '7152560f-a7f4-4183-ac4e-a4ddc5daa9e1',
  }

  private _user = new BehaviorSubject<User | null>(null);
  user$ = this._user.asObservable();

  private _isHome = new BehaviorSubject<boolean | null>(null);
  isHome$ = this._isHome.asObservable();

  private isErrorShowing: Boolean = false;

  /**
   * Users list of the current {@link Account}.
   */
  _users: User[] = [];
  _pilots: Pilot[] = [];
  _vehicles: Vehicle[] = [];
  _flightSessions: FlightSession[] = [];
  _subOrganizations: Organization[] = [];

  /**
   * Device list of the current {@link Account}.
   */
  private _devices = new BehaviorSubject<Device[]>([]);
  /**
   * Public Observable to get subscribed in.
   */
  devices$: Observable<Device[]> = this._devices.asObservable().pipe(
      tap((devices) => this.DEBUG && console.debug('Devices', devices)),
      shareReplay(1)
  );

  constructor(private http: HttpClient,
              private modalController: ModalController,
  ) {}



  // #region --> Users

  /**
   * THis method is used to retrive the Pilots List
   * @returns
   */
  getAllPilots(organizationUuid?: string): Observable<HttpResponse<MultipleApiResponse>> {
    if (organizationUuid) {
      const httpParams = new HttpParams().set('organizationId', organizationUuid);
      return this.http.get<MultipleApiResponse>(GET_ALL_PILOT, {
        withCredentials: true,
        params: httpParams,
        observe: 'response',
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          this._pilots = res?.body?.responseObject as Pilot[];

          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
    } else {
      return this.http.get<MultipleApiResponse>(GET_ALL_PILOT, {
        withCredentials: true,
        observe: 'response',
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          this._pilots = res?.body?.responseObject as Pilot[];

          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
    }
  }

  /**
   * This method is used to create a new Pilot
   * @param body
   * @returns
   */
  postPilot(body: any, organizationId?: string){
    if (organizationId) {
      const httpParams = new HttpParams().set('operatorId', organizationId);
      return this.http.post<MultipleApiResponse>(
        PILOT,

        body,
        {
          observe: 'response',
          withCredentials: true,
          params: httpParams,
        })
        .pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
    } else {
      return this.http.post<MultipleApiResponse>(
        PILOT,

        body,
        {
          observe: 'response',
          withCredentials: true,
        }
      ).pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((err: HttpErrorResponse) => {
            return throwError(() => err);
          }),
        );
    }
  }

  /**
   * This method is used to edit a Pilot
   */
  editPilot(body: any, pilotID: string){
    const httpParams = new HttpParams().set('pilotId', pilotID);
    return this.http.put<MultipleApiResponse>(
      PILOT,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
    }).pipe(
      tap((res:HttpResponse<MultipleApiResponse>) => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * This method is used to know if the user belongs to a restricted organization (Mossos, Bombers...)
   * @param user
   * @returns
   */
  userOrganizationTypeRestricted(user: User): boolean {

    if (user?.mainOrganizationUuid) {
      return !Object.values(this.restrictedOrganizations).includes(user?.mainOrganizationUuid);
    } else if (user?.organizationUuid) {
      return !Object.values(this.restrictedOrganizations).includes(user?.organizationUuid);
    }

    return true;
  }

  /**
   * This method is used to store the user in the local storage and in the service
   * @param user
   */
  setUser(user: User) {
    localStorage.setItem('user', JSON.stringify(user));
    this._user.next(user);
  }

  /**
   * THis method is used to set if the user is in the home page
   * @param isHome
   */
  setIsHome(isHome: boolean) {
    this._isHome.next(isHome);
  }

  // #endregion --> Users

  // #region --> Vehicles

  /**
   * This method is used to retrive the Vehicles List
   * @returns
   */
  getAllVehicles(organizationUuid?: string): Observable<HttpResponse<MultipleApiResponse>> {
    if (organizationUuid) {
      const httpParams = new HttpParams().set('organizationUuid', organizationUuid);
      return this.http.get<MultipleApiResponse>(GET_ALL_VEHICLE, {
        withCredentials: true,
        params: httpParams,
        observe: 'response',
      }).pipe(
        tap((res: HttpResponse<MultipleApiResponse>) => {
          this._vehicles = res?.body?.responseObject as Vehicle[];
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
    } else {
        return this.http.get<MultipleApiResponse>(GET_ALL_VEHICLE, {
          withCredentials: true,
          observe: 'response',
        }).pipe(
          tap((res: HttpResponse<MultipleApiResponse>) => {
            this._vehicles = res?.body?.responseObject as Vehicle[];
            return res;
          }),
          catchError((error) => {
            return throwError(() => error);
          })
        );
    }
  };


  /**
   * This method is used to create a new Vehicle
   * @param body
   * @returns
   */
  postVehicle(body: any, operatorId?: string){
    if (operatorId){
      const httpParams = new HttpParams().set('operatorId', operatorId);
      return this.http.post<MultipleApiResponse>(
        VEHICLE,
        body,
        {
          observe: 'response',
          withCredentials: true,
          params: httpParams
        }).pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((error: HttpErrorResponse) => {
            return throwError(() => error);
          })
        );
    } else {
      return this.http.post<MultipleApiResponse>(
        VEHICLE,
        body,
        {
          observe: 'response',
          withCredentials: true,
        }).pipe(
          tap((res:HttpResponse<MultipleApiResponse>) => {
            return res;
          }),
          catchError((error: HttpErrorResponse) => {
            return throwError(() => error);
          })
        );
    }
  }

  /**
   * This method is used to edit a Vehicle
   * @param body
   * @returns
   */
  editVehicle(body: any, vehicleId: string){
    const httpParams = new HttpParams().set('vehicleId', vehicleId);
    return this.http.put<MultipleApiResponse>(
      VEHICLE,
      body,
      {
        observe: 'response',
        withCredentials: true,
        params: httpParams,
      }).pipe(
        tap((res:HttpResponse<MultipleApiResponse>) => {
          return res;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  // #endregion --> Vehicles

  // #region --> Error Handling

  async showServerResponse(id: string | number, message: string): Promise<void> {

    let language = localStorage.getItem('lang');

    if (!language) {
      language = 'en';
    }

    if (language === 'ca') {
      language = 'cat';
    }

    let displayMessage = '';
    let messageObj;

    try {
      // Convert message to object if it's a string
      if (typeof message === 'string') {
        try {
          messageObj = JSON.parse(message);
        } catch (e) {
          messageObj = { en: message }; // Fallback if parsing fails
        }
      } else {
        messageObj = message;
      }

      // Simple language selection logic
      if (messageObj[language]) {
        displayMessage = messageObj[language];
      } else if (messageObj.en) {
        displayMessage = messageObj.en;
      } else if (Object.keys(messageObj).length > 0) {
        displayMessage = messageObj[Object.keys(messageObj)[0]];
      } else {
        displayMessage = String(message);
      }
    } catch (error) {
      displayMessage = String(message);
    }

    // Prevent multiple modals
    if (this.isErrorShowing) {
      return;
    }
    this.isErrorShowing = true;

    const modal = await this.modalController.create({
      component: HTTPResponseModalComponent,
      componentProps: {
        status: id,
        message: displayMessage
      },
      cssClass: 'http-response-modal'
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    this.isErrorShowing = false;
  }

  // #endregion --> Error Handling
}
